import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { type Columns, DataTable } from '../components/ui/data-table';
import { api, errorMessage } from '../lib/api';
import type { PriceApproval } from '../lib/types';
import { formatDateTime, formatMoney } from '../lib/utils';

const approvalsQuery = {
  queryKey: ['pricing', 'approvals'],
  queryFn: () => api<{ items: PriceApproval[] }>('/pricing/approvals'),
};

export function PriceApprovalsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const approvals = useQuery(approvalsQuery);
  const [busyId, setBusyId] = useState<string>();
  const [error, setError] = useState<string>();

  const columns = useMemo<Columns<PriceApproval>>(() => {
    const decide = async (id: string, approve: boolean) => {
      setBusyId(id);
      setError(undefined);
      try {
        await api(`/pricing/approvals/${id}/${approve ? 'approve' : 'reject'}`, { method: 'POST' });
        await queryClient.invalidateQueries({ queryKey: approvalsQuery.queryKey });
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBusyId(undefined);
      }
    };
    return [
      {
        id: 'product',
        header: t('prices.product'),
        accessorFn: (r) => r.productName ?? r.sku,
        cell: ({ row }) => (
          <Link
            to="/products/$productId"
            params={{ productId: row.original.productId }}
            className="hover:underline"
          >
            <span className="font-medium">{row.original.productName ?? '—'}</span>{' '}
            <span className="text-xs text-muted-foreground">{row.original.sku}</span>
          </Link>
        ),
      },
      { id: 'market', header: t('prices.market'), accessorFn: (r) => r.priceListCode },
      {
        id: 'old',
        header: t('prices.old'),
        accessorFn: (r) => r.approval.oldAmountMinor ?? 0,
        cell: ({ row }) =>
          formatMoney(row.original.approval.oldAmountMinor, row.original.currency, i18n.language),
      },
      {
        id: 'new',
        header: t('prices.new'),
        accessorFn: (r) => r.approval.newAmountMinor,
        cell: ({ row }) => (
          <span className="font-medium">
            {formatMoney(
              row.original.approval.newAmountMinor,
              row.original.currency,
              i18n.language,
            )}
          </span>
        ),
      },
      {
        id: 'delta',
        header: t('prices.delta'),
        accessorFn: (r) => Number(r.approval.deltaPct ?? 0),
        cell: ({ row }) => {
          const delta = Number(row.original.approval.deltaPct ?? 0);
          return (
            <Badge variant={delta > 0 ? 'warning' : 'secondary'}>
              {delta > 0 ? '+' : ''}
              {delta.toFixed(1)}%
            </Badge>
          );
        },
      },
      { id: 'reason', header: t('prices.reason'), accessorFn: (r) => r.approval.reason ?? '' },
      {
        id: 'created',
        header: t('prices.created'),
        accessorFn: (r) => r.approval.createdAt,
        cell: ({ row }) => formatDateTime(row.original.approval.createdAt, i18n.language),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const id = row.original.approval.id;
          return (
            <span className="flex gap-1">
              <Button size="sm" disabled={busyId === id} onClick={() => void decide(id, true)}>
                {t('prices.approve')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busyId === id}
                onClick={() => void decide(id, false)}
              >
                {t('prices.reject')}
              </Button>
            </span>
          );
        },
      },
    ];
  }, [t, i18n.language, queryClient, busyId]);

  return (
    <>
      <PageHeader title={t('prices.title')} description={t('prices.hint')} />
      {approvals.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(approvals.error)}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="destructive" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={approvals.data?.items ?? []}
        loading={approvals.isPending}
        rowKey={(r) => r.approval.id}
      />
    </>
  );
}
